import React, { useState, useEffect, useRef } from "react";
import { View, Pressable } from "react-native-web";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import CustomText from "../components/atoms/CustomText";
import { colors, spacing, } from "../utils/styling";
import PersonalityQuestion from "../components/common/PersonalityQuestion";
import Button from "../components/common/Button";
import CircularProgress from "@mui/material/CircularProgress";
import ProcessingResults from "../components/common/ProcessingResults";
import PrimingCard from "../components/common/PrimingCard";
import SvgArrow from "../components/icons/SvgArrow";
import {
  useCreateQuiz,
  useSubmitQuizAnswers,
  QuizAnswer,
  useUpdateQuizWithSocials,
  useUpdateQuizWithInfo,
} from "../lib/quizzes";
import { useResponsive } from "../hooks/useResponsive";
import { QUESTIONS } from "../utils/quizinfo/questions";
import {
  shouldShowPrimingPage,
  getPrimingPage,
} from "../utils/quizinfo/priming";
import { estimatedQuizMinutes } from "../utils/quizinfo/estimate";
import { ADVISOR_ATTRIBUTION_KEY } from "../utils/quizRetake";

const QUESTIONS_PER_SECTION = 3;
const NUM_SECTIONS = Math.ceil(QUESTIONS.length / QUESTIONS_PER_SECTION);

// Intro-copy counts are derived from the question data, never hardcoded. Two separate intro
// blocks previously hardcoded "17" and "15" while the quiz actually had 17 — they disagreed
// with each other AND with reality, and a typecheck can't catch prose. (ClickUp 86bb0f9rv)
const MAIN_QUESTION_COUNT = QUESTIONS.length;

// 2026-08-20 (McKayla Loom "Current Site Feedback Part 3" t=1:59): both intro
// blocks hardcoded "Est. time to complete: 2 minutes" above a 29-question
// assessment. This is the SAME defect class the count fix above was written
// for — prose a typecheck cannot catch, disagreeing with reality — so the
// estimate is derived too, never restated. Do NOT hand-edit this to a literal.
// Rate + rationale live in utils/quizinfo/estimate.ts, which the test imports
// so the page and its pin can never drift apart.
const ESTIMATED_MINUTES = estimatedQuizMinutes(MAIN_QUESTION_COUNT);

// Build index lists for personality vs profile questions in the main quiz
const PERSONALITY_QUESTION_INDICES: number[] = QUESTIONS.reduce(
  (acc: number[], q: any, idx: number) => {
    if (q.type === "personality") acc.push(idx);
    return acc;
  },
  [] as number[]
);

const PROFILE_QUESTION_INDICES: number[] = QUESTIONS.reduce(
  (acc: number[], q: any, idx: number) => {
    if (q.type === "profile") acc.push(idx);
    return acc;
  },
  [] as number[]
);

// Helper: calculate category scores using only personality questions from full answer sheet
const calculateCategoryScoresFromFullAnswers = (
  fullAnswers: (number | undefined)[]
): Record<string, number> => {
  const categoryCount: Record<string, number> = {};
  let totalPersonalityAnswered = 0;

  PERSONALITY_QUESTION_INDICES.forEach((qIdx) => {
    const answerIndex = fullAnswers[qIdx];
    if (answerIndex !== undefined) {
      const question = QUESTIONS[qIdx];
      const selectedOption: any = question.options[answerIndex];
      if (selectedOption && selectedOption.category) {
        totalPersonalityAnswered += 1;
        categoryCount[selectedOption.category] =
          (categoryCount[selectedOption.category] || 0) + 1;
      }
    }
  });

  const categoryScores: Record<string, number> = {};
  Object.keys(categoryCount).forEach((category) => {
    categoryScores[category] =
      totalPersonalityAnswered > 0
        ? (categoryCount[category] / totalPersonalityAnswered) * 100
        : 0;
  });

  return categoryScores;
};

// calculateSpecificCategoryScores was removed on 2026-08-20 with the
// sub-personality quiz (ClickUp 86bb0f9vz). Nothing scores sub-types now.

// Helper function to find the dominant category
const getDominantCategory = (
  categoryScores: Record<string, number>
): string => {
  let maxScore = 0;
  let dominantCategory = "";

  Object.entries(categoryScores).forEach(([category, score]) => {
    if (score > maxScore) {
      maxScore = score;
      dominantCategory = category;
    }
  });

  return dominantCategory;
};

// PrimeSpecificCard ("You're Almost There — Only 5 more questions to go") and
// SingleSpecificQuestion were REMOVED on 2026-08-20 with the sub-personality
// quiz (ClickUp 86bb0f9vz). The card existed only to sell the extra five
// questions; both die with the phase they introduced.

const SectionedPersonalityTest: React.FC<{
  onProcessingChange: (isProcessing: boolean) => void;
  onProcessingStart: (answers: (number | undefined)[]) => void;
  onAnswerUpdate: (answers: (number | undefined)[]) => void;
  showSingle?: boolean;
  question?: any;
  value?: number | undefined;
  onChange?: (value: number) => void;
  showInstructions?: boolean;
  isMainQuiz: boolean;
  currentQuestionIndex?: number;
  onPrimingDismiss?: () => void;
}> = ({
  onProcessingChange,
  onProcessingStart,
  onAnswerUpdate,
  showSingle=false,
  question="",
  value,
  onChange,
  showInstructions=false,
  isMainQuiz,
  currentQuestionIndex=0,
  onPrimingDismiss,
}) => {
  const { isMobile } = useResponsive();
  const [section, setSection] = useState(0);
  const [answers, setAnswers] = useState<(number | undefined)[]>(
    Array(QUESTIONS.length).fill(undefined)
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPrimingPage, setShowPrimingPage] = useState(false);
  const [mobileQuestionIdx, setMobileQuestionIdx] = useState(0);
  const [currentPrimingPage, setCurrentPrimingPage] = useState<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const startIdx = section * QUESTIONS_PER_SECTION;
  const endIdx = Math.min(startIdx + QUESTIONS_PER_SECTION, QUESTIONS.length);
  const sectionQuestions = QUESTIONS.slice(startIdx, endIdx);

  // Refs for each question in the section
  const questionRefs = useRef<(HTMLDivElement | null)[]>([]);

  const handleAnswer = (idx: number, value: number) => {
    const globalIdx = startIdx + idx;
    setAnswers((prev) => {
      const next = [...prev];
      next[globalIdx] = value;
      onAnswerUpdate(next);
      return next;
    });

    // Desktop: Scroll to the next question in the section
    if (!isMobile && idx < sectionQuestions.length - 1) {
      const nextRef = questionRefs.current[idx + 1];
      if (nextRef) {
        nextRef.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
    if (value && onChange) onChange(value);
  };

  // Navigation logic
  const canGoNext = isMobile
    ? showSingle 
      ? answers[currentQuestionIndex] !== undefined
      : answers[startIdx + mobileQuestionIdx] !== undefined
    : answers.slice(startIdx, endIdx).every((a) => a !== undefined);
  const canGoBack = isMobile
    ? showSingle
      ? currentQuestionIndex > 0
      : section > 0 || mobileQuestionIdx > 0
    : section > 0;
  const isLastSection = section === NUM_SECTIONS - 1;

  // Priming logic - handled by parent component for mobile single mode
  const lastQuestionInSection = endIdx - 1;
  // Scroll to first question when section changes (but not on initial load)
  useEffect(() => {
    if (section > 0 && questionRefs.current[0]) {
      questionRefs.current[0].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
    setMobileQuestionIdx(0); // reset mobile question index on section change
  }, [section]);

  // Next button handler
  const handleNext = () => {
    if (isMobile) {
      if (showSingle) {
        // In showSingle mode, the parent handles navigation
        // Let parent handle the next question
        // This will be handled by the parent's navigation
      } else {
        // If not last question in section, go to next question
        if (mobileQuestionIdx < sectionQuestions.length - 1) {
          setMobileQuestionIdx((idx) => idx + 1);
        } else {
          // Last question in section: check for priming
          const shouldShowPriming = shouldShowPrimingPage(lastQuestionInSection);
          const primingPage = getPrimingPage(lastQuestionInSection);
          if (shouldShowPriming && primingPage) {
            setShowPrimingPage(true);
          } else if (!isLastSection) {
            setSection((s) => s + 1);
          } else {
            handleFinish();
          }
        }
      }
    } else {
      // Desktop: section navigation
      // Only check for priming if we're completing the current section (all questions answered)
      if (canGoNext) {
        const shouldShowPriming = shouldShowPrimingPage(lastQuestionInSection);
        const primingPage = getPrimingPage(lastQuestionInSection);
        if (shouldShowPriming && primingPage) {
          console.log('showing prime????')
          setShowPrimingPage(true);
          setCurrentPrimingPage(primingPage);
        } else if (!isLastSection) {
          setSection((s) => s + 1);
        } else {
          handleFinish();
        }
      }
    }
  };

  // Back button handler
  const handleBack = () => {
    if (isMobile) {
      if (showSingle) {
        // In showSingle mode, the parent handles navigation
        // Let parent handle the back navigation
        // This will be handled by the parent's navigation
      } else {
        if (mobileQuestionIdx > 0) {
          setMobileQuestionIdx((idx) => idx - 1);
        } else if (section > 0) {
          setSection((s) => s - 1);
          setMobileQuestionIdx(QUESTIONS_PER_SECTION - 1); // go to last question of previous section
        }
      }
    } else {
      const shouldShowPriming = shouldShowPrimingPage(lastQuestionInSection);
      const primingPage = getPrimingPage(lastQuestionInSection);
      if (shouldShowPriming && primingPage) {
        setShowPrimingPage(true);
      } else {
        setSection((s) => s - 1);
      }
    }
  };

  const handlePrimingContinue = () => {
    setShowPrimingPage(false);
    setCurrentPrimingPage(null);
    if (showSingle) {
      // In showSingle mode, notify parent to advance to next question
      if (onPrimingDismiss) {
        onPrimingDismiss();
      }
    } else if (!isLastSection) {
      setSection((s) => s + 1);
    } else {
      handleFinish();
    }
  };

  const handleFinish = () => {
    setIsProcessing(true);
    onProcessingChange(true);
    onProcessingStart(answers);
  };

  if (isProcessing) {
    return null;
  }

  // Just a single question for mobile? 
  if (showSingle) {
    return(
      <View
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: isMobile ? spacing.md : spacing.xl,
        paddingLeft: isMobile ? spacing.lg : spacing.xxl,
        paddingRight: isMobile ? spacing.lg : spacing.xxl,
        marginBottom: spacing.sm,
      }}
    >
      {/* Instructions card - only show on first question */}
      {showInstructions && (
        <View
          style={{
            backgroundColor: colors.white,
            borderRadius: 16,
            border: "1px solid black",
            padding: isMobile ? spacing.md : spacing.xl,
            maxWidth: 1000,
            width: "100%",
            marginBottom: spacing.xxl,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          <View
            style={{
              display: "flex",
              flexDirection: "column",
              flexWrap: "wrap",
              alignItems: "flex-start",
              marginBottom: 12,
            }}
          >
            <CustomText
              style={{
                fontSize: isMobile ? 16 : 24,
                fontFamily: "'Nunito', sans-serif",
                fontWeight: 400,
                wordBreak: "break-word",
                whiteSpace: "normal",
                textAlign: "center",
              }}
            >
              You are about to begin the <b>Money Personality</b> quiz. This
              assessment is a series of {MAIN_QUESTION_COUNT} questions.
            </CustomText>
          </View>

          <CustomText
            style={{
              fontSize: isMobile ? 16 : 24,
              color: "black",
              fontFamily: "'Nunito', sans-serif",
              textAlign: "center",
            }}
          >
            Est. time to complete: {ESTIMATED_MINUTES} minutes
          </CustomText>
        </View>
      )}

      {/* Current question */}
      {question && (
        <View
          style={{
            backgroundColor: colors.white,
            borderRadius: 16,
            border: "1px solid black",
            padding: isMobile ? spacing.md : spacing.xl,
            maxWidth: 1400,
            width: "100%",
            marginBottom: spacing.lg,
          }}
        >
          <PersonalityQuestion
            id={`question-${question.question}`}
            question={question.question}
            value={value}
            onChange={onChange}
            options={question.options}
            shuffle={(question as any)?.type !== 'profile'}
            helper={(question as any)?.helper}
          />
        </View>
      )}
    </View>
    );
  }

  return (
    <View
      ref={containerRef}
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: isMobile ? spacing.md : spacing.xl,
        paddingLeft: isMobile ? spacing.lg : spacing.xxl,
        paddingRight: isMobile ? spacing.lg : spacing.xxl,
        marginBottom: spacing.xxl,
      }}
    >
      {/* Show either priming card or questions */}
      {showPrimingPage && currentPrimingPage ? (
        <PrimingCard
          title={currentPrimingPage.title}
          content={currentPrimingPage.content}
          onContinue={handlePrimingContinue}
          continueButtonText={currentPrimingPage.continueButtonText}
        />
      ) : (
        <>
          {/* Instructions card - only show on first section */}
          {section === 0 && (
            <View
              style={{
                backgroundColor: colors.white,
                borderRadius: 16,
                border: "1px solid black",
                padding: isMobile ? spacing.md : spacing.xl,
                maxWidth: 1000,
                width: "100%",
                marginBottom: spacing.xxl,
                marginLeft: "auto",
                marginRight: "auto",
              }}
            >
              <View
                style={{
                  display: "flex",
                  flexDirection: "column",
                  flexWrap: "wrap",
                  alignItems: "flex-start",
                  marginBottom: 12,
                }}
              >
                <CustomText
                  style={{
                    fontSize: isMobile ? 16 : 24,
                    fontFamily: "'Nunito', sans-serif",
                    fontWeight: 400,
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                    textAlign: "center",
                  }}
                >
                  You are about to begin the <b>Money Personality</b> quiz.
                  This assessment is a series of {MAIN_QUESTION_COUNT}{" "}
                  questions.
                </CustomText>
              </View>

              <CustomText
                style={{
                  fontSize: isMobile ? 16 : 24,
                  color: "black",
                  fontFamily: "'Nunito', sans-serif",
                  textAlign: "center",
                }}
              >
                Est. time to complete: {ESTIMATED_MINUTES} minutes
              </CustomText>
            </View>
          )}

          {/* Render questions */}
          {isMobile
            ? // Mobile: show one question at a time
              sectionQuestions.map((q, i) =>
                i === mobileQuestionIdx ? (
                  <View
                    key={q.question}
                    ref={(el: HTMLDivElement | null) => {
                      questionRefs.current[i] = el;
                    }}
                    style={{
                      backgroundColor: colors.white,
                      borderRadius: 16,
                      border: "1px solid black",
                      padding: spacing.md,
                      maxWidth: 1400,
                      width: "100%",
                      marginBottom: spacing.xxl,
                    }}
                  >
                    <PersonalityQuestion
                      id={`question-${startIdx + i}`}
                      question={q.question}
                      value={answers[startIdx + i]}
                      onChange={(v) => handleAnswer(i, v)}
                      options={q.options}
                      shuffle={(q as any)?.type !== 'profile'}
                      helper={(q as any)?.helper}
                    />
                  </View>
                ) : null
              )
            : // Desktop: show all questions in section
              sectionQuestions.map((q, i) => (
                <View
                  key={q.question}
                  ref={(el: HTMLDivElement | null) => {
                    questionRefs.current[i] = el;
                  }}
                  style={{
                    backgroundColor: colors.white,
                    borderRadius: 16,
                    border: "1px solid black",
                    padding: spacing.xl,
                    maxWidth: 1400,
                    width: "100%",
                    marginBottom: spacing.xxl,
                  }}
                >
                  <PersonalityQuestion
                    id={`question-${startIdx + i}`}
                    question={q.question}
                    value={answers[startIdx + i]}
                    onChange={(v) => handleAnswer(i, v)}
                    options={q.options}
                    shuffle={(q as any)?.type !== 'profile'}
                    helper={(q as any)?.helper}
                  />
                </View>
              ))}
        </>
      )}

      {/* Navigation buttons */}
      {!showPrimingPage && (
        <View
          style={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
            justifyContent: "center",
            alignItems: "center",
            marginTop: spacing.xl,
            gap: spacing.md,
          }}
        >
          {canGoBack && (
            <Button
              variant="secondary"
              label="Back"
              style={{
                minWidth: 90,
                height: 36,
                paddingLeft: 12,
                paddingRight: 12,
              }}
              textStyle={{
                fontSize: 14,
              }}
              disabled={!canGoBack}
              onPress={handleBack}
            />
          )}
          <Pressable
            data-testid="next-button"
            style={{
              backgroundColor: canGoNext ? "#748469" : colors.gray[300],
              paddingVertical: isMobile ? spacing.md : spacing.lg,
              paddingLeft: isMobile ? spacing.xl : spacing.xxl,
              paddingRight: isMobile ? spacing.xl : spacing.xxl,
              borderRadius: 999,
              cursor: canGoNext ? "pointer" : "not-allowed",
              transition: "all 0.2s",
              opacity: canGoNext ? 1 : 0.5,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: spacing.md,
              minWidth: isMobile ? 150 : 350,
              justifyContent: "center",
              marginLeft: canGoBack ? spacing.lg : 0,
            }}
            disabled={!canGoNext}
            onPress={handleNext}
          >
            <CustomText
              style={{
                color: colors.white,
                fontSize: isMobile ? 16 : 20,
                fontWeight: "600",
                fontFamily: "Nunito",
              }}
            >
              {isLastSection && (!isMobile || mobileQuestionIdx === sectionQuestions.length - 1)
                ? "Finish"
                : "Next"}
            </CustomText>
            <View
              style={{
                transform: "rotate(180deg)",
                marginLeft: 0,
              }}
            >
              <SvgArrow color={colors.white} width={isMobile ? 16 : 22} height={isMobile ? 12 : 18} />
            </View>
          </Pressable>
        </View>
      )}
    </View>
  );
};

const QuizPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const sfAccount = params.clientId;
  const { isMobile } = useResponsive();

  // An advisor's marketing link / QR opens /quiz?advisor=<public_id> (86bagxuwz). Keep
  // the id until the lead is saved so the lead is credited to that advisor, the same
  // attribution the /onboarding intake already does. The backend ignores an unknown id.
  useEffect(() => {
    const advisor = new URLSearchParams(location.search).get("advisor");
    if (advisor && /^[0-9a-f-]{36}$/i.test(advisor)) {
      localStorage.setItem(ADVISOR_ATTRIBUTION_KEY, advisor);
    }
  }, [location.search]);
  const [showProcessingOverlay, setShowProcessingOverlay] =
    useState(false);
  const [testAnswers, setTestAnswers] = useState<(number | undefined)[]>(
    Array(QUESTIONS.length).fill(undefined)
  );
  const [, setIsProcessing] = useState(false);
  // `isSubmitting` used to be driven by the sub-quiz's last-question handler.
  // That handler is gone, so the save below drives it now — otherwise the
  // button sits idle while the quiz is actually being written.
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ⛔ IDEMPOTENCY GUARD — one completed quiz must create exactly ONE row.
  //
  // handleMainQuizComplete has FOUR call sites: the footer Next button on the
  // last question, and SectionedPersonalityTest's own handleFinish (which is
  // itself reachable three ways), wired for both the mobile and desktop
  // layouts. That fan-in was harmless until 2026-08-20, when the sub-quiz was
  // removed and quiz creation moved INTO this handler — a browser run against
  // a local stub then recorded TWO `POST /quizzes` for a single completion.
  //
  // A ref, not state: setState is async, so two calls in the same tick would
  // both read `isSubmitting === false` and both submit. This bounds the RESULT
  // rather than trying to enumerate and de-duplicate the callers.
  const hasSubmittedRef = useRef(false);
  const [showReviewsCard, setShowReviewsCard] = useState(false);
  const [showInfoCard, setShowInfoCard] = useState(false);
  const [quizResults, setQuizResults] = useState<any>(null);
  const [currQuizId, setCurrQuizId] = useState<number | null>(null);

  // New state for centralized question management
  const [currentMainQuestionIndex, setCurrentMainQuestionIndex] =
    useState(0);
  const [showInstructions, setShowInstructions] = useState(true);
  const [showPrimingPage, setShowPrimingPage] = useState(false);
  const [currentPrimingPage, setCurrentPrimingPage] = useState<any>(null);

  useEffect(() => {
    console.log("Quiz page loaded with sfAccount:", sfAccount);
  }, [sfAccount]);

  // Broadcast initial question index on mount
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('quizQuestionIndexChanged', {
      detail: { 
        currentIndex: currentMainQuestionIndex,
        totalQuestions: QUESTIONS.length,
        direction: 'initial'
      }
    }));
  }, [currentMainQuestionIndex]); // TODO do we only run on mount? 

  // Add quiz mutation hooks
  const { mutateAsync: createQuiz } = useCreateQuiz();
  const { mutateAsync: submitAnswers } = useSubmitQuizAnswers();
  const { mutateAsync: updateQuizWithSocials } = useUpdateQuizWithSocials();
  const { mutateAsync: updateQuizWithInfo } = useUpdateQuizWithInfo();

  // Navigation handlers for main quiz
  const handleMainQuizNext = () => {
    // Check if there should be a priming page after the current question
    const shouldShowPriming = shouldShowPrimingPage(currentMainQuestionIndex);
    const primingPage = getPrimingPage(currentMainQuestionIndex);

    if (shouldShowPriming && primingPage) {
      // Show priming page instead of advancing to next question
      setShowPrimingPage(true);
      setCurrentPrimingPage(primingPage);
      return;
    }

    if (currentMainQuestionIndex < QUESTIONS.length - 1) {
      const newIndex = currentMainQuestionIndex + 1;
      setCurrentMainQuestionIndex(newIndex);
      setShowInstructions(false);
      
      // Broadcast the updated index
      window.dispatchEvent(new CustomEvent('quizQuestionIndexChanged', {
        detail: { 
          currentIndex: newIndex,
          totalQuestions: QUESTIONS.length,
          direction: 'next'
        }
      }));
    } else {
      // Last question reached, complete main quiz
      handleMainQuizComplete(testAnswers);
    }
  };

  const handleMainQuizBack = () => {
    if (currentMainQuestionIndex > 0) {
      const newIndex = currentMainQuestionIndex - 1;
      setCurrentMainQuestionIndex(newIndex);
      
      // Broadcast the updated index
      window.dispatchEvent(new CustomEvent('quizQuestionIndexChanged', {
        detail: { 
          currentIndex: newIndex,
          totalQuestions: QUESTIONS.length,
          direction: 'back'
        }
      }));
    }
  };

  // Get current question based on state
  const getCurrentMainQuestion = () => {
    return QUESTIONS[currentMainQuestionIndex] || null;
  };

  // Check if we can proceed to next question
  const canProceedMainQuiz = () => {
    return testAnswers[currentMainQuestionIndex] !== undefined;
  };

  // Check if we can go back
  const canGoBackMainQuiz = () => {
    return currentMainQuestionIndex > 0;
  };

  // Handle answer selection
  const handleMainQuizAnswer = (value: number) => {
    const newAnswers = [...testAnswers];
    newAnswers[currentMainQuestionIndex] = value;
    setTestAnswers(newAnswers);
  };

  const handlePrimingDismiss = () => {
    // Clear priming page state
    setShowPrimingPage(false);
    setCurrentPrimingPage(null);
    
    // Advance to the next question after priming page is dismissed
    if (currentMainQuestionIndex < QUESTIONS.length - 1) {
      const newIndex = currentMainQuestionIndex + 1;
      setCurrentMainQuestionIndex(newIndex);
      setShowInstructions(false);
      
      // Broadcast the updated index
      window.dispatchEvent(new CustomEvent('quizQuestionIndexChanged', {
        detail: { 
          currentIndex: newIndex,
          totalQuestions: QUESTIONS.length,
          direction: 'next'
        }
      }));
    } else {
      // Last question reached, complete main quiz
      handleMainQuizComplete(testAnswers);
    }
  };

  const handleProcessingComplete = () => {
    setShowProcessingOverlay(false);
    setTestAnswers([]);
    // navigate('/quiz/results/submit', { state: { from: location.pathname }});
  };

  // ⛔ THIS IS THE ONLY PATH THAT PERSISTS A QUIZ. Read before editing.
  //
  // Until 2026-08-20 quiz creation and submission lived inside
  // handleSpecificQuizComplete — the LAST step of the sub-personality quiz — so
  // finishing the main questions saved nothing on its own. Removing the sub-quiz
  // (ClickUp 86bb0f9vz) therefore meant moving createQuiz + submitAnswers here,
  // not just deleting a phase. If this function stops awaiting both calls, the
  // quiz silently records nothing and the user still sees their results, because
  // the results page reads local state. That failure is invisible until someone
  // looks for the row.
  //
  // The old version also carried `if (!subQuiz) return;` — an early return that
  // skipped submission entirely for any personality with no sub-quiz defined.
  // Deliberately not reproduced: there is no longer anything to look up, and a
  // silent return before a save is exactly the shape that hides data loss.
  const handleMainQuizComplete = async (answers: (number | undefined)[]) => {
    // Compute dominant category from personality-only answers
    const categoryScores = calculateCategoryScoresFromFullAnswers(answers);
    const dominant = getDominantCategory(categoryScores);

    // Aggregate profile answers and persist for later (SignUp submits)
    const profileAnswers: Record<string, string> = {};
    try {
      PROFILE_QUESTION_INDICES.forEach((qIdx) => {
        const answerIndex = answers[qIdx];
        if (answerIndex !== undefined) {
          const q = QUESTIONS[qIdx];
          const opt = q.options[answerIndex];
          if (opt && opt.text) profileAnswers[q.question] = opt.text;
        }
      });
      if (Object.keys(profileAnswers).length > 0) {
        localStorage.setItem("mpay_personalInfo", JSON.stringify(profileAnswers));
      }
    } catch (e) {
      console.error("Failed to save personal info to localStorage", e);
    }

    if (hasSubmittedRef.current) return;
    hasSubmittedRef.current = true;
    setIsSubmitting(true);
    try {
      // Build personality-only answers (ordered by personality question indices)
      const personalityAnsweredPairs: Array<{ qIdx: number; aIdx: number }> = [];
      PERSONALITY_QUESTION_INDICES.forEach((qIdx) => {
        const a = answers[qIdx];
        if (a !== undefined) personalityAnsweredPairs.push({ qIdx, aIdx: a });
      });
      const mainAnswered = personalityAnsweredPairs.map((p) => p.aIdx);

      const quiz = await createQuiz(sfAccount || undefined);

      const formattedMainAnswers = personalityAnsweredPairs.map(
        ({ qIdx, aIdx }, idx) => {
          const question = QUESTIONS[qIdx];
          const selectedOption: any = question.options[aIdx];
          return {
            question_id: idx + 1,
            question: question.question,
            answer: selectedOption.text,
            category: selectedOption.category,
          } as QuizAnswer;
        }
      );

      // `results` carries mainQuiz only. The specificQuiz key is intentionally
      // absent rather than sent as {} — every reader already treats a missing
      // sub-result as "no sub-personality", and an empty object would score as a
      // real-but-empty result.
      await submitAnswers({
        quizId: quiz.id,
        answers: formattedMainAnswers,
        results: { mainQuiz: categoryScores },
      });

      // Save the profile answers (age group, net worth, ...) onto the quiz row.
      // Before this they lived only in localStorage, so the admin Leads report
      // showed N/A for every quiz taker (Nina 2026-09-23). Non-fatal: a failure
      // here must not cost the person their results.
      if (Object.keys(profileAnswers).length > 0) {
        try {
          await updateQuizWithInfo({ quizId: quiz.id, info: profileAnswers });
        } catch (infoError) {
          console.error("Failed to save profile answers to quiz:", infoError);
        }
      }
      setCurrQuizId(quiz.id);

      const resultsData = {
        answers: mainAnswered,
        categoryScores,
        dominantCategory: dominant,
        sfAccount: sfAccount,
        quizId: quiz.id,
        // Email is collected on the contact form for everyone, including advisor-link
        // clients. The public email-by-account lookup that pre-filled it is gone: it
        // let anyone read a client's email from their Salesforce id (2026-09-24).
        email: undefined,
        from: location.pathname,
      };

      setQuizResults(resultsData);
      setShowReviewsCard(true);
    } catch (error) {
      console.error("Error completing quiz:", error);
      // Release the latch so the user can retry a failed save. Without this a
      // network blip would silently cost them their whole quiz.
      hasSubmittedRef.current = false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReviewContinue = () => {
    setShowReviewsCard(false);
    setShowInfoCard(true);
  };

  const handleInfoContinue = async (socials: string[]) => {
    if (currQuizId === null) {
      console.error("Tried updating quiz info with a NULL quiz ID");
      return;
    }

    try {
      await updateQuizWithSocials({
        quizId: currQuizId,
        socials,
      });
    } catch (error) {
      console.error("Failed to update quiz with socials:", error);
    }

    // Navigate based on sfAccount, no longer using info-form route
    if (quizResults) {
      localStorage.setItem("mpay_resultsData", JSON.stringify(quizResults));
      if (quizResults.sfAccount && quizResults.email) {
        navigate("/quiz/results-ready", {
          state: { ...quizResults, from: location.pathname },
        });
      } else {
        navigate("/quiz/results/submit", {
          state: { ...quizResults, from: location.pathname },
        });
      }
      setShowInfoCard(false);
    }
  };

  const handleAnswerUpdate = (answers: (number | undefined)[]) => {
    setTestAnswers(answers);
  };

  const ProgressFooter = () => {
    const mainAnsweredCount = testAnswers.filter(
      (a): a is number => a !== undefined
    ).length;
    const totalAnswered = mainAnsweredCount;
    // Derived, not hardcoded — this was `22 // 17 main + 5 specific` and silently went stale the
    // moment the question set changed, showing "0 of 22" against a 24-question quiz. (ClickUp 86bb0f9rv)
    const totalQuestions = MAIN_QUESTION_COUNT;
    const progress = totalQuestions > 0 ? totalAnswered / totalQuestions : 0;

    const isMainQuiz = !showReviewsCard && !showInfoCard && !showPrimingPage;
    const showBackButton = isMainQuiz && canGoBackMainQuiz();
    const showNextButton = isMainQuiz && !showPrimingPage;
    const canProceed = canProceedMainQuiz();
    const isLast = currentMainQuestionIndex === QUESTIONS.length - 1;

    return (
      <View
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: colors.white,
          borderTop: `1px solid ${colors.gray[200]}`,
          padding: isMobile ? spacing.sm : spacing.md,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: isMobile ? "space-around" : "center",
          gap: isMobile ? spacing.xs : spacing.md,
          zIndex: 100,
          boxShadow: "0 -1px 6px rgba(0,0,0,0.04)",
        }}
      >
        {isMobile ? (
          <>
            {/* Back Button */}
            <View
              style={{
                display: "flex",
                flexDirection: "row",
                width: 80,
                justifyContent: 'center',
              }}
            >
              {/* Back button - show if we can go back in either quiz */}
              <Pressable
                style={{
                  backgroundColor: colors.gray[200],
                  paddingVertical: spacing.xs,
                  paddingHorizontal: spacing.sm,
                  borderRadius: 8,
                  cursor: showBackButton ? "pointer" : "default",
                  width: '100%', // Fixed width to maintain spacing
                  height: 32, // Fixed height to maintain spacing
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  visibility: showBackButton ? "visible" : "hidden",
                }}
                onPress={handleMainQuizBack}
                disabled={!showBackButton}
              >
                <CustomText
                  style={{
                    color: colors.dark,
                    fontSize: 14,
                    fontWeight: "600",
                    fontFamily: "Nunito",
                  }}
                >
                  Back
                </CustomText>
              </Pressable>
            </View>
            {/* Progress section */}
            <View
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: 'center',
              }}
            >
              <View
                style={{
                  width: isMobile ? 150 : 200,
                  height: 8,
                  backgroundColor: colors.gray[200],
                  borderRadius: 4,
                  overflow: "hidden",
                  flexShrink: 0,
                  marginHorizontal: isMobile ? 10 : 0,
                }}
              >
                <View
                  style={{
                    width: `${progress * 100}%`,
                    height: "100%",
                    backgroundColor: colors.primary,
                    transition: "width 0.3s ease-in-out",
                  }}
                />
              </View>
              <CustomText
                style={{
                  color: colors.gray[700],
                  fontSize: isMobile ? 13 : 16,
                  fontFamily: "Nunito",
                  whiteSpace: "nowrap",
                }}
              >
                {Math.round(progress * 100)}%
              </CustomText>
            </View>
            {/* Next Button */}
            <View
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: 'center',
                width: 80,
              }}
            >
              <Pressable style={{
                width: '100%',
                height: 32,
                display: 'flex',
                flexDirection :'row',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: canProceed && showNextButton ? '#748469' : colors.gray[300],
                paddingVertical: spacing.xs,
                paddingHorizontal: spacing.sm,
                borderRadius: 8,
                cursor: canProceed && showNextButton ? "pointer" : "not-allowed",
                opacity: canProceed && showNextButton ? 1 : 0.5,
                visibility: (showPrimingPage || showInfoCard || showReviewsCard) ? "hidden" : "visible",
                }}
                disabled={!canProceed || !showNextButton}
                onPress={handleMainQuizNext}
              >
                <CustomText
                  style={{
                    color: colors.white,
                    fontSize: 14,
                    fontWeight: "600",
                    fontFamily: "Nunito",
                  }}
                >
                  {isSubmitting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    isLast ? "Finish" : "Next"
                  )}
                </CustomText>
              </Pressable>
            </View>
          </>
        ) : (
          <>
            <CustomText
              style={{
                color: colors.gray[700],
                fontSize: isMobile ? 14 : 16,
                fontFamily: "Nunito",
                whiteSpace: "nowrap",
              }}
            >
              {totalAnswered} of {totalQuestions} answered
            </CustomText>
            <View
              style={{
                width: isMobile ? 150 : 200,
                height: 8,
                backgroundColor: colors.gray[200],
                borderRadius: 4,
                overflow: "hidden",
                flexShrink: 0,
                marginHorizontal: isMobile ? 10 : 0,
              }}
            >
              <View
                style={{
                  width: `${progress * 100}%`,
                  height: "100%",
                  backgroundColor: colors.primary,
                  transition: "width 0.3s ease-in-out",
                }}
              />
            </View>
            <CustomText
              style={{
                color: colors.gray[700],
                fontSize: isMobile ? 13 : 16,
                fontFamily: "Nunito",
                whiteSpace: "nowrap",
              }}
            >
              {Math.round(progress * 100)}%
            </CustomText>
            {/* Next/Finish Button */}
          </>
        )}
      </View>
    );
  };

  return (
    <View style={{ flex: 1, position: "relative", minHeight: isMobile ? undefined : "100vh" }}>
      <View
        style={{
          flex: 1,
          alignItems: "center",
          justifyContent: isMobile ? "flex-start" : "center",
          padding: isMobile ? spacing.sm : spacing.xl,
          paddingBottom: isMobile ? spacing.xxl : spacing.xl, // Extra padding for mobile to account for the footer
        }}
      >
        {!showReviewsCard &&
          !showInfoCard &&
          !showPrimingPage &&
          (isMobile ? (
            // Mobile: Show one question at a time
            <SectionedPersonalityTest
              onProcessingChange={setIsProcessing}
              onProcessingStart={handleMainQuizComplete}
              onAnswerUpdate={handleAnswerUpdate}
              showSingle={true}
              isMainQuiz={true}
              question={getCurrentMainQuestion()}
              value={testAnswers[currentMainQuestionIndex]}
              onChange={handleMainQuizAnswer}
              showInstructions={showInstructions}
              currentQuestionIndex={currentMainQuestionIndex}
              onPrimingDismiss={handlePrimingDismiss}
            />
          ) : (
            // Desktop: Show all questions in sections (existing behavior)
            <SectionedPersonalityTest
              onProcessingChange={setIsProcessing}
              onProcessingStart={handleMainQuizComplete}
              onAnswerUpdate={handleAnswerUpdate}
              isMainQuiz={true}
            />
          ))}

        {showPrimingPage && currentPrimingPage && (
          <PrimingCard
            title={currentPrimingPage.title}
            content={currentPrimingPage.content}
            onContinue={handlePrimingDismiss}
            continueButtonText={currentPrimingPage.continueButtonText}
          />
        )}

        {showReviewsCard && (
          <PrimingCard
            type="reviews"
            title="The Money Personality&trade; was made for people like you."
            content="Congratulations! You've successfully completed your Money Personality assessment. Your results are ready and we're excited to share them with you."
            onContinue={handleReviewContinue}
            continueButtonText="Continue"
          />
        )}

        {showInfoCard && (
          <PrimingCard
            type="info"
            title="Where did you hear about us?"
            content="We've analyzed your responses and created a personalized Money Personality profile just for you. To view your detailed results, we'll need a few quick details from you."
            onContinue={handleInfoContinue}
            continueButtonText="Continue"
          />
        )}
      </View>
      {showProcessingOverlay && (
        <View
          style={{
            backgroundColor: colors.white,
            borderRadius: 16,
            border: "1px solid black",
            padding: isMobile ? spacing.md : spacing.xl,
            maxWidth: 1400,
            width: "100%",
            marginBottom: spacing.xxl,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <ProcessingResults
            answers={testAnswers.filter(
              (answer): answer is number => answer !== undefined
            )}
            onReset={handleProcessingComplete}
            clientId={sfAccount}
          />
        </View>
      )}
      <ProgressFooter />
    </View>
  );
};

// SpecificQuizSection (the desktop all-at-once sub-personality question list)
// was removed on 2026-08-20 with the sub-personality quiz (ClickUp 86bb0f9vz).
// Nothing outside this file ever imported it.

export { QuizPage };
